import { createElement, type ComponentType } from "react";

import {
  AccordionItem,
  AccordionItemBody,
  AccordionItemContent,
  AccordionItemContext,
  AccordionItemIndicator,
  AccordionItemTrigger,
  AccordionRoot,
  CloseButton,
  Dialog as ChakraDialog,
  Drawer as ChakraDrawer,
  Popover as ChakraPopover
} from "@chakra-ui/react";

import { asSlotComponent } from "./internal/v3-slot";

// v3 `CloseTrigger` is an unstyled button; render a default `CloseButton` (via
// `asChild`) when no children are passed, so it looks like v2's auto X icon.
function makeCloseTrigger<T>(Trigger: T): ComponentType<any> {
  const C = Trigger as unknown as ComponentType<any>;
  return function WrappedCloseTrigger({ children, ...props }: any) {
    if (children) {
      return createElement(C, props, children);
    }
    return createElement(
      C,
      { asChild: true, ...props },
      createElement(CloseButton, {
        variant: "ghost",
        // Explicit dims instead of `size` so the iconButton recipe doesn't override.
        boxSize: "8",
        minW: "8",
        minH: "8",
        fontSize: "xs",
        fontWeight: "normal",
        p: 0
      })
    );
  };
}

const AccordionItemTriggerSlot = asSlotComponent(AccordionItemTrigger);

function AccordionTrigger(props: Record<string, unknown>) {
  return createElement(AccordionItemTriggerSlot, {
    cursor: "pointer",
    _disabled: { cursor: "not-allowed" },
    ...props
  });
}

export const Accordion = {
  Root: AccordionRoot,
  Item: asSlotComponent(AccordionItem),
  ItemTrigger: AccordionTrigger,
  ItemContent: asSlotComponent(AccordionItemContent),
  ItemBody: asSlotComponent(AccordionItemBody),
  ItemIndicator: asSlotComponent(AccordionItemIndicator),
  ItemContext: AccordionItemContext
};

export const Dialog = {
  Root: ChakraDialog.Root,
  Backdrop: asSlotComponent(ChakraDialog.Backdrop),
  Positioner: asSlotComponent(ChakraDialog.Positioner),
  Content: asSlotComponent(ChakraDialog.Content),
  Header: asSlotComponent(ChakraDialog.Header),
  Body: asSlotComponent(ChakraDialog.Body),
  Footer: asSlotComponent(ChakraDialog.Footer),
  CloseTrigger: makeCloseTrigger(ChakraDialog.CloseTrigger),
  Title: asSlotComponent(ChakraDialog.Title)
};

export const Drawer = {
  Root: ChakraDrawer.Root,
  Backdrop: asSlotComponent(ChakraDrawer.Backdrop),
  Positioner: asSlotComponent(ChakraDrawer.Positioner),
  Content: asSlotComponent(ChakraDrawer.Content),
  Header: asSlotComponent(ChakraDrawer.Header),
  Body: asSlotComponent(ChakraDrawer.Body),
  Footer: asSlotComponent(ChakraDrawer.Footer),
  CloseTrigger: makeCloseTrigger(ChakraDrawer.CloseTrigger)
};

export const Popover = {
  Root: ChakraPopover.Root,
  Trigger: asSlotComponent(ChakraPopover.Trigger),
  Positioner: asSlotComponent(ChakraPopover.Positioner),
  Content: asSlotComponent(ChakraPopover.Content),
  Body: asSlotComponent(ChakraPopover.Body),
  Arrow: asSlotComponent(ChakraPopover.Arrow),
  Header: asSlotComponent(ChakraPopover.Header),
  Footer: asSlotComponent(ChakraPopover.Footer)
};
